import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListCustomBlogPosts,
  adminUpsertCustomBlogPost,
  adminDeleteCustomBlogPost,
} from "@/lib/blog/admin.functions";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin/blog")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Blog Admin — DrHint" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BlogAdmin,
});

const SAMPLE_SPEC = `{
  "slug": "how-to-check-imei-number-pakistan-india",
  "title": "How to Check IMEI Number in Pakistan & India (2026 Complete Guide)",
  "metaTitle": "Check IMEI Number — Free Code + Online Method (2026)",
  "description": "Free IMEI check guide: dial *#06#, verify with PTA/DIRBS or India TAFCOP, spot fake IMEI, and unlock stolen-device checks — all in one page.",
  "focusKeyword": "IMEI check",
  "keywords": ["imei check","mobile imei check code","how to check imei","imei check karne ka tarika"],
  "date": "2026-07-21",
  "readMinutes": 7,
  "category": "Mobile Codes",
  "author": { "name": "DrHint Editorial" },
  "heroBadge": "Free · Verified 2026",
  "heroTagline": "Every method to find, verify and validate your phone's IMEI in under 30 seconds.",
  "tldr": [
    "Dial *#06# on any phone — IMEI shows instantly.",
    "Cross-check in Pakistan on the PTA DIRBS portal.",
    "In India, verify on ceir.gov.in or dial *#*#4636#*#*."
  ],
  "sections": [
    {
      "type": "codeBlock",
      "heading": "The universal IMEI check code",
      "label": "Dial on any phone",
      "code": "*#06#",
      "note": "Works on Android, iPhone, Nokia, KaiOS — even a locked screen."
    },
    {
      "type": "paragraph",
      "heading": "What is an IMEI number?",
      "text": [
        "IMEI (International Mobile Equipment Identity) is a 15-digit serial that identifies your specific handset on the GSM network. Every phone sold worldwide carries a unique IMEI, and dual-SIM phones carry two.",
        "Carriers use IMEI to grant network access, block stolen devices, and validate warranty."
      ]
    }
  ],
  "faq": [
    { "q": "What if *#06# doesn't work?", "a": "On some CDMA devices dial *#*#4636#*#* → Phone information; otherwise open Settings → About phone → Status." }
  ],
  "related": [
    { "title": "Telenor Number Check Code", "url": "/blog/telenor-number-check-code" }
  ]
}`;

function BlogAdmin() {
  const [state, setState] = useState<"loading" | "anon" | "not-admin" | "admin">("loading");
  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return setState("anon");
      const { data } = await supabase.from("user_roles").select("role")
        .eq("user_id", user.id).eq("role", "admin").maybeSingle();
      setState(data ? "admin" : "not-admin");
    })().catch(() => setState("anon"));
  }, []);

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="text-2xl font-bold">✍️ Blog — Admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste a Blog JSON spec to add or update a rich blog post. Every published
          post auto-broadcasts to IndexNow and enters the indexing queue.
        </p>
        {state === "loading" && <div className="mt-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin" /></div>}
        {state === "anon" && <p className="mt-6 text-sm">Please sign in from <Link to="/admin-portal" className="text-primary underline">/admin-portal</Link> first.</p>}
        {state === "not-admin" && <p className="mt-6 text-sm">Your account is not an admin.</p>}
        {state === "admin" && <Dashboard />}
      </section>
    </Layout>
  );
}

type Row = { slug: string; title: string; is_published: boolean; updated_at: string };

function Dashboard() {
  const listFn = useServerFn(adminListCustomBlogPosts);
  const upsertFn = useServerFn(adminUpsertCustomBlogPost);
  const deleteFn = useServerFn(adminDeleteCustomBlogPost);
  const [rows, setRows] = useState<Row[]>([]);
  const [json, setJson] = useState<string>(SAMPLE_SPEC);
  const [published, setPublished] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function reload() {
    try {
      const r = await listFn();
      setRows((r as { items: Row[] }).items);
    } catch (e) { setMsg((e as Error).message); }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true); setMsg("Validating & saving…");
    try {
      const spec = JSON.parse(json);
      const res = await upsertFn({ data: { spec, is_published: published } });
      setMsg(`Saved ✓  Live at ${(res as { url: string }).url}`);
      await reload();
    } catch (e) {
      setMsg(`❌ ${(e as Error).message}`);
    } finally { setBusy(false); }
  }

  async function remove(slug: string) {
    if (!confirm(`Delete ${slug}?`)) return;
    setBusy(true);
    try {
      await deleteFn({ data: { slug } });
      setMsg(`Deleted ${slug}`);
      await reload();
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  }

  async function loadIntoEditor(slug: string) {
    setBusy(true);
    try {
      const { data, error } = await supabase.from("custom_blog_posts").select("spec").eq("slug", slug).maybeSingle();
      if (error) throw error;
      if (data?.spec) setJson(JSON.stringify(data.spec, null, 2));
      setMsg(`Loaded ${slug}`);
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-6 space-y-6">
      <section className="rounded-lg border-2 border-primary/30 bg-card p-4 space-y-3">
        <h2 className="text-lg font-bold">Add or update a blog post (JSON spec)</h2>
        <p className="text-xs text-muted-foreground">
          Blocks: <code>paragraph</code>, <code>callout</code>, <code>cards</code>,
          <code> steps</code>, <code>table</code>, <code>quote</code>, <code>codeBlock</code>,
          <code> faq</code>. Every heading auto-creates a table-of-contents pill and anchor.
          Add 20–60 related <code>keywords</code> to rank on hundreds of long-tail variations.
        </p>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          spellCheck={false}
          rows={22}
          className="w-full rounded-md border border-border bg-background p-3 font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Published
          </label>
          <button disabled={busy} onClick={save} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            {busy ? <Loader2 className="inline h-4 w-4 animate-spin" /> : "Save"}
          </button>
          <button type="button" onClick={() => setJson(SAMPLE_SPEC)} className="rounded-md border border-border px-3 py-2 text-xs">
            ↺ Reset to sample
          </button>
          {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
        </div>
      </section>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Post</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">No custom posts yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.slug}>
                <td className="px-3 py-2">
                  <Link to="/blog/$slug" params={{ slug: r.slug }} className="text-primary hover:underline">
                    {r.title}
                  </Link>
                  <div className="text-[10px] text-muted-foreground">{r.slug}</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.is_published ? "Published" : "Draft"}</td>
                <td className="px-3 py-2 text-xs">{new Date(r.updated_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 space-x-2">
                  <button onClick={() => loadIntoEditor(r.slug)} className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">Edit</button>
                  <button onClick={() => remove(r.slug)} className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}